import { useEffect, useMemo, useState } from 'react';
import type { PairChoice, PairChoiceOption } from './lib/session-core';
import { VisualProviderCredit } from './VisualProviderCredit';
import { t } from './lib/i18n';

const isLineArt = (value: string | null | undefined): boolean => Boolean(value?.includes('/visuals/line/'));

export function PairChoiceGrid({
  roundId,
  options,
  myAnswerKey,
  pendingChoice,
  disabled,
  onChoose
}: {
  roundId: string;
  options: PairChoiceOption[];
  myAnswerKey: PairChoice | null;
  pendingChoice: PairChoice | null;
  disabled: boolean;
  onChoose: (choice: PairChoice, label: string) => void;
}) {
  const [primaryFailed, setPrimaryFailed] = useState<Set<PairChoice>>(new Set());
  const [hidden, setHidden] = useState<Set<PairChoice>>(new Set());

  useEffect(() => {
    setPrimaryFailed(new Set());
    setHidden(new Set());
  }, [roundId]);

  const sources = useMemo(() => Object.fromEntries(options.map((option) => {
    if (hidden.has(option.key)) return [option.key, null];
    const source = primaryFailed.has(option.key)
      ? option.fallbackImageUrl
      : option.imageUrl ?? option.fallbackImageUrl;
    return [option.key, source ?? null];
  })) as Record<PairChoice,string|null>, [options, primaryFailed, hidden]);
  const visual = options.some((option) => Boolean(sources[option.key]));

  const handleImageError = (option: PairChoiceOption): void => {
    const source = sources[option.key];
    if (source && option.fallbackImageUrl && source !== option.fallbackImageUrl) {
      setPrimaryFailed((current) => new Set(current).add(option.key));
      return;
    }
    setHidden((current) => new Set(current).add(option.key));
  };

  return (
    <>
    <div className={`${options.length > 2 ? 'answer-options four-choice-grid' : 'answer-options'}${visual ? ' visual-choice-grid' : ''}`}>
      {options.map((option) => {
        const selected = myAnswerKey === option.key || pendingChoice === option.key;
        const status = myAnswerKey === option.key ? t('✓ LOCKED') : pendingChoice === option.key ? t('CONFIRMING…') : null;
        const source = sources[option.key];
        return (
          <button
            key={`${roundId}-${option.key}`}
            className={selected ? 'answer-option selected' : 'answer-option'}
            aria-pressed={selected}
            aria-label={`${option.label}${myAnswerKey === option.key ? t(', locked') : pendingChoice === option.key ? t(', pending confirmation') : ''}`}
            disabled={disabled || Boolean(myAnswerKey) || Boolean(pendingChoice)}
            onClick={() => onChoose(option.key, option.label)}
          >
            {source ? (
              <span className={`answer-choice-media${isLineArt(source) ? ' line-art' : ''}`} aria-hidden="true">
                <img
                  className={`answer-choice-image${isLineArt(source) ? ' line-art' : ''}`}
                  src={source}
                  alt=""
                  decoding="async"
                  loading="eager"
                  onError={() => handleImageError(option)}
                />
              </span>
            ) : null}
            <span className="answer-choice-label">{option.label}</span>
            {status ? <small>{status}</small> : null}
          </button>
        );
      })}
    </div>
    <VisualProviderCredit alts={options.map((option) => option.imageAlt)} />
    </>
  );
}
